import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Container, Row, Col } from 'reactstrap';
import { Description, Copyright } from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';

// Import CSS styles for react-pdf layers
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Configure PDFJS Worker using the unpkg CDN matching the installed version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const UserManual = () => {
  const pdfUrl = `${process.env.PUBLIC_URL}/manuals/user_manual.pdf`;
  const [numPages, setNumPages] = useState(null);
  const [containerWidth, setContainerWidth] = useState(800); // Default fallback width
  const containerRef = useRef(null);

  // Handle PDF document load success
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Measure the width of the parent container to keep the PDF pages responsive
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Subtract some padding to fit nicely inside the scroll wrapper
        setContainerWidth(Math.max(rect.width - 60, 300));
      }
    };

    updateWidth();

    // Trigger update after a short delay to ensure DOM layout is complete
    const timer = setTimeout(updateWidth, 100);

    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, []);

  return (
    <Container className="dashboard">
      <Row>
        <Col md={12}>
          <h3 className="page-title">User Manual</h3>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Card>
            <CardBody>
              {/* Header Action Bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Description color="primary" />
                  <span style={{ fontWeight: 600, fontSize: '16px' }}>System Documentation & User Manual</span>
                </div>
              </div>

              {/* PDF Container with scrolling, right-click, and text selection prevention */}
              <div 
                ref={containerRef}
                style={{ 
                  width: '100%', 
                  height: '80vh', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '4px', 
                  overflowY: 'auto',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '20px 0'
                }}
                onContextMenu={(e) => e.preventDefault()} // Prevents right-click save/print menu
                onCopy={(e) => e.preventDefault()}       // Blocks document copying
              >
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<div style={{ padding: '20px', textAlign: 'center' }}>Loading PDF Document...</div>}
                  error={<div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>Failed to load PDF. Please try again.</div>}
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <div 
                      key={`page-container_${index + 1}`}
                      style={{ 
                        marginBottom: '25px', 
                        boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                        backgroundColor: '#ffffff'
                      }}
                    >
                      <Page 
                        pageNumber={index + 1} 
                        width={containerWidth}
                        renderTextLayer={true}       // Allows search/accessibility (but copy is disabled via onCopy)
                        renderAnnotationLayer={true} // Allows hyperlinks inside the PDF to work
                      />
                    </div>
                  ))}
                </Document>
              </div>

              {/* Copyright Notice */}
              <div
                style={{
                  marginTop: '15px',
                  paddingTop: '12px',
                  borderTop: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  color: '#6c757d',
                }}
              >
                <Copyright style={{ fontSize: 16 }} />
                <span>
                  {new Date().getFullYear()} Informap. All rights reserved.
                </span>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UserManual;
